import { useState, useEffect, useRef, type ChangeEvent, type KeyboardEvent } from 'react'
import {
  fetchPlaceSuggestions,
  getPlaceLocationDetails,
  type PlaceSuggestion,
} from '../services/googleMapsPlaces'
import type { LocationGeoData } from '../utils/salzburgLocation'

export interface ItineraryLocationSearchInputProps {
  label: 'FROM' | 'TO'
  initialValue?: string
  placeholder: string
  isRestrictedToSalzburg: boolean
  onSelect: (location: LocationGeoData) => void
  onCancel: () => void
}

export default function ItineraryLocationSearchInput({
  label,
  initialValue = '',
  placeholder,
  isRestrictedToSalzburg,
  onSelect,
  onCancel,
}: ItineraryLocationSearchInputProps) {
  const [query, setQuery] = useState(initialValue)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Focus input automatically on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onCancel()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [onCancel])

  // Live search debounced
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmed = query.trim()
    if (trimmed.length < 2) {
      setSuggestions([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await fetchPlaceSuggestions(trimmed, isRestrictedToSalzburg)
        setSuggestions(results)
        setSelectedIndex(-1)
      } catch (err) {
        console.error('Failed to fetch place suggestions:', err)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 280)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [query, isRestrictedToSalzburg])

  const handleSelectSuggestion = async (suggestion: PlaceSuggestion) => {
    setIsLoading(true)
    try {
      const fullLocation = await getPlaceLocationDetails(suggestion)
      onSelect(fullLocation)
    } catch {
      onSelect({
        title: suggestion.title,
        address: suggestion.address,
        isSalzburg: suggestion.isSalzburg,
        placeId: suggestion.placeId,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
      return
    }

    if (suggestions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[selectedIndex])
      }
    }
  }

  const getIconElement = (type?: 'airport' | 'station' | 'hotel' | 'pin') => {
    switch (type) {
      case 'airport':
        return (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
            </svg>
          </span>
        )
      case 'station':
        return (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect width="16" height="16" x="4" y="3" rx="2" />
              <path d="M4 11h16" />
              <path d="M12 3v8" />
              <path d="m8 19-2 3" />
              <path d="m18 22-2-3" />
              <circle cx="8" cy="15" r="1" />
              <circle cx="16" cy="15" r="1" />
            </svg>
          </span>
        )
      case 'hotel':
        return (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-600">
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z" />
              <path d="M9 22v-4h6v4" />
              <path d="M8 6h.01" />
              <path d="M16 6h.01" />
              <path d="M12 6h.01" />
              <path d="M12 10h.01" />
              <path d="M12 14h.01" />
              <path d="M16 10h.01" />
              <path d="M16 14h.01" />
              <path d="M8 10h.01" />
              <path d="M8 14h.01" />
            </svg>
          </span>
        )
      default:
        return (
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
            <svg
              className="h-3 w-3"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </span>
        )
    }
  }

  const isDropdownVisible = query.trim().length >= 2

  return (
    <div ref={containerRef} className="relative mt-2 w-full">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full rounded-[10px] border border-[#365E92] bg-white px-3 py-2 text-[14px] font-medium text-black placeholder:text-[#9CA3AF] shadow-sm outline-none focus:ring-2 focus:ring-[#365E92]/20"
          />

          {isLoading ? (
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-orange border-t-transparent" />
            </div>
          ) : query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('')
                setSuggestions([])
                if (inputRef.current) inputRef.current.focus()
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] font-bold text-[#9CA3AF] hover:text-black"
              aria-label="Clear input"
            >
              ✕
            </button>
          ) : null}
        </div>

        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 rounded-[8px] bg-[#EAEAEA] px-2.5 py-2 font-poppins text-[12px] font-semibold text-[#555555] hover:bg-[#E0E0E0] hover:text-black transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Suggestions dropdown */}
      {isDropdownVisible && (
        <div
          role="listbox"
          aria-label={`${label} live location suggestions`}
          className="
            absolute left-0 top-[calc(100%+6px)] z-[150]
            max-h-[260px] w-full min-w-[280px]
            overflow-y-auto rounded-[12px] bg-white
            p-1.5 shadow-[0_12px_30px_rgba(0,0,0,0.18)]
            border border-[#E5E9EB]
          "
        >
          {suggestions.length > 0 ? (
            suggestions.map((suggestion, idx) => {
              const isSelected = idx === selectedIndex

              return (
                <button
                  key={suggestion.placeId || `${suggestion.title}-${idx}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  className={`
                    flex w-full items-start gap-2.5 rounded-[8px] px-2.5 py-2 text-left transition-colors
                    ${isSelected ? 'bg-[#F2F4F7]' : 'hover:bg-[#F9FAFB]'}
                  `}
                >
                  <div className="mt-0.5">
                    {getIconElement(suggestion.iconType)}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-poppins text-[13px] font-semibold leading-snug text-black truncate">
                      {suggestion.title}
                    </p>
                    <p className="mt-0.5 font-poppins text-[11px] leading-tight text-[#6B7280] line-clamp-1">
                      {suggestion.address}
                    </p>
                  </div>
                </button>
              )
            })
          ) : !isLoading ? (
            <div className="py-3 text-center font-poppins text-[12px] text-[#71717A]">
              No matching locations found
            </div>
          ) : null}

          <div className="mt-1 flex items-center justify-between border-t border-[#F0F2F5] px-2 pt-1 font-poppins text-[9px] text-[#9CA3AF]">
            <span>Google Maps Places</span>
            <span>Live Europe Geocoding</span>
          </div>
        </div>
      )}
    </div>
  )
}
